import styled from 'styled-components';
import flex from './../../styles/flex';
import { applyTheme, getThemeColor, hover, when } from './../../styles/mixins';
import { breakpoints, smaller } from './../../styles/responsive';
import Icon from './../../icons/Icon';

export const Contact = styled.div({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start'
});

export const Content = styled.div({
  width: 'min(900px, 100%)',
  borderRadius: 16,
  padding: 'clamp(18px, 2.2vw, 30px)',
  background: 'rgba(26, 40, 55, 0.94)',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  boxShadow: '0 14px 38px rgba(0, 0, 0, 0.32)'
});

export const SectionTitle = styled.h3({
  margin: 0,
  fontSize: 'clamp(24px, 3vw, 30px)',
  fontWeight: 700,
  letterSpacing: 0.25
});

export const SectionSubtitle = styled.p({
  margin: '6px 0 0',
  opacity: 0.86,
  lineHeight: 1.5,
  fontSize: 15
});

export const HeaderBlock = styled.div({
  marginBottom: 18,
  lineHeight: 1.6
});

export const OrgName = styled.span({
  fontSize: 'clamp(18px, 0.8vw + 14px, 22px)',
  fontWeight: 600,
  letterSpacing: 0.2
});

export const MetaLink = styled.a(
  {
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'all 120ms linear'
  },
  getThemeColor('text'),
  hover({
    borderBottom: '1px solid #58a6ff'
  })
);

export const Divider = styled.div({
  height: 1,
  width: '100%',
  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)',
  margin: '14px 0 16px'
});

export const USNep = styled.div({
  ...flex.horizontal,
  gap: 10,
  margin: '8px 0 18px',
  [smaller(breakpoints.medium)]: {
    flexDirection: 'column'
  }
});

export const Country = styled.button(
  {
    ...flex.horizontal,
    ...flex.centerHorizontal,
    userSelect: 'none',
    alignItems: 'center',
    gap: 8,
    border: '1px solid rgba(88, 166, 255, 0.45)',
    fontSize: 'clamp(13px, 0.3vw + 11px, 14px)',
    borderRadius: 10,
    padding: '12px 16px',
    width: '100%',
    minHeight: 44,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    transition: 'all 150ms linear'
  },
  applyTheme('country'),
  when('disabled', {
    opacity: 0.5,
    cursor: 'not-allowed'
  }),
  ({ theme }) => ({
    ...(!theme.disabled && {
      ...hover({
        background: 'rgba(88, 166, 255, 0.18)',
        borderColor: 'rgba(88, 166, 255, 0.9)'
      })
    })
  })
);

export const CountryLabel = styled.span({
  display: 'block',
  width: '100%',
  textAlign: 'center'
});

export const NameEmail = styled.div({
  ...flex.horizontal,
  gap: 14,
  [smaller(breakpoints.medium)]: {
    flexDirection: 'column'
  }
});

export const FieldGroup = styled.div({
  marginTop: 12,
  flex: 1
});

export const FieldLabel = styled.label({
  display: 'block',
  marginBottom: 0,
  fontSize: 'clamp(12px, 0.25vw + 10px, 13px)',
  letterSpacing: 0.2,
  opacity: 0.9
});

const baseInputStyle = {
  borderRadius: 10,
  padding: '11px 14px',
  border: '1px solid rgba(255,255,255,0.2)',
  outline: 'none',
  width: '100%',
  resize: 'none',
  fontSize: 'clamp(13px, 0.3vw + 11px, 14px)',
  lineHeight: 1.45,
  boxSizing: 'border-box',
  background: 'rgba(14, 24, 35, 0.75)',
  color: 'white',
  transition: 'border-color 120ms ease, box-shadow 120ms ease, background 120ms ease',
  '&[aria-invalid="true"]': {
    borderColor: '#ff8a8a',
    boxShadow: '0 0 0 2px rgba(255,138,138,0.18)'
  },
  '&:focus': {
    borderColor: '#58a6ff',
    boxShadow: '0 0 0 2px rgba(88,166,255,0.22)',
    background: 'rgba(14, 24, 35, 0.95)'
  }
};

export const Input = styled.input(baseInputStyle, {
  minHeight: 44
}, applyTheme('contactInput'));

export const InputMessage = styled.textarea(baseInputStyle, {
  minHeight: 128,
  resize: 'vertical'
}, applyTheme('contactInput'));

export const ButtonRow = styled.div({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 16
});

export const ButtonLabel = styled.span({
  display: 'inline-block',
  textAlign: 'center'
});

export const Button = styled.button(
  {
    ...flex.horizontal,
    ...flex.centerHorizontal,
    userSelect: 'none',
    alignItems: 'center',
    gap: 8,
    border: '1px solid rgba(88, 166, 255, 0.7)',
    fontSize: 'clamp(14px, 0.4vw + 11px, 16px)',
    borderRadius: 10,
    padding: '12px 16px',
    width: 'auto',
    maxWidth: '100%',
    minHeight: 44,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    transition: 'all 150ms linear',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.26)',
    background: 'linear-gradient(180deg, rgba(88,166,255,0.28), rgba(88,166,255,0.12))'
  },
  applyTheme('contactbutton'),
  when('disabled', {
    opacity: 0.5,
    cursor: 'not-allowed'
  }),
  ({ theme }) => ({
    ...(!theme.disabled && {
      ...hover({
        background: 'linear-gradient(180deg, rgba(88,166,255,0.36), rgba(88,166,255,0.16))',
        borderColor: '#58a6ff'
      })
    })
  })
);

export const AIcon = styled(Icon)(
  {
    marginRight: 0,
    width: 18,
    height: 18,
    position: 'relative',
    top: -1,
    opacity: 1
  },
  { fill: '#ffffff' }
);


export const FieldError = styled.p({
  margin: '6px 0 0',
  color: '#ff8a8a',
  fontSize: '12px',
  lineHeight: 1.3
});

export const Notice = styled.p({
  textAlign: 'center',
  margin: '12px 0 0',
  fontSize: 'clamp(13px, 0.3vw + 11px, 14px)',
  opacity: 0.95
});

export const Map = styled.div({});
